import React, { useState } from 'react';
import { t } from '@lingui/macro';
import { useQueryClient } from '@tanstack/react-query';

import { Box, Button, Heading, Paragraph } from '@rover/kibble/core';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import { actions as currentUserActions } from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import {
  CurrentUserViewSchema,
  getApiCurrentUserRetrieveQueryKey,
  useApiAccountHomeAddressUpdateCreate,
  UserInformation,
  UserInformationAddress,
} from '@rover/rsdk/src/apiClient/latest';
import type { ErrorType } from '@rover/rsdk/src/modules/Network/orvalHTTPInterface';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import { KibbleAddressForm } from '@rover/shared/js/components/KibbleAddressForm';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useDucks from '@rover/shared/js/hooks/useDucks';
import emitMetric from '@rover/utilities/emitMetric';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import useHomeAddress from '../hooks/useHomeAddress';

interface ErrorResponse {
  errors?: Record<string, string[]>;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (newAddressData: UserInformationAddress) => void;
};

export default function HomeEditModal({ isOpen, onClose, onEdit }: Props): JSX.Element {
  const { address } = useHomeAddress();
  const { user } = useCurrentUserShim();
  const isMobile = isMobileBrowser();
  const queryClient = useQueryClient();
  const [submissionErrors, setSubmissionErrors] = useState<Record<string, string[]>>({});

  // Get Redux actions for updating current user state
  const { data: reduxActions } = useDucks({
    actions: { fetchCurrentUserSuccess: currentUserActions.fetchCurrentUserSuccess },
  });

  // API mutation hook for updating home address
  const { mutate, isLoading } = useApiAccountHomeAddressUpdateCreate({
    mutation: {
      onSuccess: (data) => {
        emitMetric('search-edit-home-address-save', { source: 'web' });
        fireDataLayerEvent({ event: 'search-edit-home-address-save' });
        const newAddressData = address
          ? {
              ...address,
              addressLine1: data.addressLine1,
              addressLine2: data.addressLine2,
              city: data.city,
              state: data.state,
              zip: data.zip,
              countryCode: data.countryCode,
              latitude: data.latitude,
              longitude: data.longitude,
            }
          : null;

        if (newAddressData && onEdit) {
          onEdit(newAddressData);
        }

        // Update Redux state for legacy pages that don't use React Query
        // Get the current React Query cache data and manually update it with the new address
        const currentUserData = queryClient.getQueryData(
          getApiCurrentUserRetrieveQueryKey()
        ) as CurrentUserViewSchema;
        if (currentUserData && currentUserData.authenticated && 'currentUser' in currentUserData) {
          const updatedUserData: CurrentUserViewSchema = {
            ...currentUserData,
            currentUser: {
              ...currentUserData.currentUser,
              address: newAddressData,
            },
          };
          reduxActions.fetchCurrentUserSuccess(updatedUserData);
        }

        onClose();
      },

      onError: (error: ErrorType<Error>) => {
        if ('rawResponse' in error && error.rawResponse) {
          const errorData = error.rawResponse.data as ErrorResponse;
          if (errorData?.errors) {
            setSubmissionErrors(errorData.errors);
          }
        }
      },
    },
  });

  const isProvider = (currentUser?: UserInformation): boolean => {
    return currentUser?.userMode === 'provider';
  };

  const handleFormSubmit = (data): void => {
    mutate({ data });
  };

  const renderHeader = (): JSX.Element => (
    <>
      {isMobile && <Heading size="300" mb="2x">{t`Edit home address`}</Heading>}
      <Paragraph size={isMobile ? '100' : '200'}>
        {t`Update your current home address to find the best sitters nearby.`}
      </Paragraph>
    </>
  );

  const display = isMobile
    ? { Component: BottomSheet, props: { hideCloseIcon: true } }
    : { Component: Modal, props: { showCloseIcon: true, headerText: t`Edit home address` } };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <display.Component isOpen={isOpen} onRequestClose={onClose} {...display.props}>
        {renderHeader()}
        <KibbleAddressForm
          onSubmit={handleFormSubmit}
          defaultValues={address || undefined}
          disableCountry={isProvider(user)}
          isLoading={isLoading}
          submissionErrors={submissionErrors}
        />
        <Box display="flex" justifyContent="center">
          <Button variant="flat" onClick={onClose} width="100%" size="small">
            {t`Cancel`}
          </Button>
        </Box>
      </display.Component>
    </Box>
  );
}
